import React, { useCallback, useEffect, useRef, useState } from "react";
import { setDJFormVisibility } from "@/store/djMessageForm/action";
import { RootState } from "@/store/store";
import { connect, ConnectedProps } from "react-redux";
require("@/styles/djMessageForm.less");

import SendOutlined from "@ant-design/icons/SendOutlined";

import { Button, Form, Input, Rate, Popover } from "antd";
import { sendMessage, sendRating } from "./utils";
import { API_ENDPOINT } from "@/utils/general";
import Reaptcha from "reaptcha";

const { TextArea } = Input;

// Simple FSM
const SUBMISSION_STATE = {
  NON_SUBMITTED: 0,
  POSTING: 1,
  POSTED: 2,
};

const mapState = (state: RootState) => ({
  visible: state.djMessageForm.visible,
});

const mapDispatch = {
  setDJFormVisibility,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & {
  onSentMessage: (msg: string) => void;
};

const DJMessageForm = ({
  onSentMessage,
  visible,
  setDJFormVisibility,
}: Props): JSX.Element => {
  const [form] = Form.useForm();

  const [rateVisible, setRateVisible] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  const captcha = useRef<Reaptcha>(null);

  const [submissionState, setSubmissionState] = useState(
    SUBMISSION_STATE.NON_SUBMITTED
  );

  const onVerify = useCallback(function (token) {
    submitForm(token);
  }, []);

  const rateShow = useCallback((rating) => {
    setHasRated(true);
    sendRating(rating);

    // close rating after 1 second
    const closeRatingTimeout = setTimeout(function () {
      setRateVisible(false);
      setSubmissionState(SUBMISSION_STATE.NON_SUBMITTED);
    }, 2000);
    return () => {
      clearTimeout(closeRatingTimeout);
    };
  }, []);

  const closeDrawer = useCallback(() => {
    setDJFormVisibility(false);
    setRateVisible(false);
  }, [setDJFormVisibility]);

  const closeRater = useCallback(() => {
    setRateVisible(false);
  }, []);

  useEffect(() => {
    if (!visible) {
      setRateVisible(false);
    }
  }, [visible]);

  const [, forceUpdate] = useState(undefined);

  const submitForm = useCallback(
    (token) => {
      let toClearTimeout: ReturnType<typeof setTimeout>;
      async function asyncFunc() {
        setSubmissionState(SUBMISSION_STATE.POSTING);

        const formData = {
          captchaResponse: token,
          ...form.getFieldsValue(),
        };

        try {
          await sendMessage(formData);

          onSentMessage(formData.message);

          if (!hasRated) {
            setRateVisible(true);
          }
          // on successful post
          setSubmissionState(SUBMISSION_STATE.POSTED);

          form.resetFields();

          toClearTimeout = setTimeout(() => {
            setSubmissionState(SUBMISSION_STATE.NON_SUBMITTED);
          }, 5000);
        } catch (e) {
          form.setFields([
            {
              name: "message",
              errors: [e.toString()],
            },
          ]);
          console.error("Failed to send message!");
        }
      }

      asyncFunc();

      return () => {
        if (toClearTimeout != null) {
          clearTimeout(toClearTimeout);
        }
      };
    },
    [form, onSentMessage, hasRated]
  );

  useEffect(() => {
    window["djFormSubmit"] = (token: unknown) => {
      submitForm(token);
    };
    return () => {
      window["djFormSubmit"] = undefined;
    };
  }, [submitForm]);

  // To disable submit button at the beginning.
  useEffect(() => {
    forceUpdate({});
  }, []);

  const onFinishForm = useCallback(
    function () {
      if (captcha.current == null) {
        return;
      }
      // verify captcha
      captcha.current.execute();
    },
    [captcha]
  );

  return (
    <>
      <Form
        name="dj_submission_form"
        method="POST"
        action={API_ENDPOINT + "/purple-radio/messages"}
        form={form}
        onBlur={closeRater}
        onFinish={onFinishForm}
        // onFinish={onFinishCallback}
        // onFinishFailed={() => console.log("tes2")}
      >
        <Form.Item
          name="message"
          rules={[{ required: true, message: "Please fill in a message" }]}
          hasFeedback
        >
          <TextArea rows={3} showCount maxLength={280} />
        </Form.Item>
        {/* <Form.Item>Rate the show</Form.Item> */}
        {/* <Form.Item name="rate" label="Rate">
        <Rate />
      </Form.Item> */}
        <div style={{ textAlign: "right" }}>
          <Form.Item shouldUpdate={true} noStyle>
            {() => (
              <Popover
                arrowPointAtCenter
                trigger="none"
                placement="topRight"
                visible={rateVisible}
                overlayClassName={"rate-popover"}
                destroyTooltipOnHide
                content={
                  !hasRated ? (
                    <>
                      Rate the show: <Rate allowHalf onChange={rateShow} />
                    </>
                  ) : (
                    <>Thank You!</>
                  )
                }
              >
                <Reaptcha
                  ref={captcha}
                  onVerify={onVerify}
                  sitekey={
                    process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_V3 ||
                    "6LeHZ9sZAAAAAJfjn3PPO_hhhFQqpRGuy-N7nYtM"
                  }
                  size={"invisible"}
                />

                <Button
                  type={
                    submissionState !== SUBMISSION_STATE.POSTED
                      ? `default`
                      : `dashed`
                  }
                  htmlType="submit"
                  className={
                    "submit-form-btn" +
                    (!form.isFieldsTouched(true) ||
                    !!form
                      .getFieldsError()
                      .filter(({ errors }) => errors.length).length ||
                    submissionState === SUBMISSION_STATE.POSTED
                      ? " disabled"
                      : "")
                  }
                  loading={submissionState === SUBMISSION_STATE.POSTING}
                >
                  {submissionState !== SUBMISSION_STATE.POSTED ? (
                    `Send`
                  ) : (
                    <>
                      <SendOutlined /> `Sent`
                    </>
                  )}
                </Button>
              </Popover>
            )}
          </Form.Item>
          <Form.Item noStyle>
            <Button onClick={closeDrawer} style={{ marginRight: 8 }}>
              Close
            </Button>
          </Form.Item>
        </div>
      </Form>
    </>
  );
};

export default connector(DJMessageForm);
